// DriverSelect — Phase 4 Plan 04-03 (DIS-07).
//
// Controlled component: search-and-pick driver for trip assignment.
// Debounced 300ms; uses driversApi.list with `search` query param.

import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { type DriverListItem, driversApi } from "../api/drivers";
import { useAuth } from "../auth/useAuth";
import { TOUCH_TARGET_MIN } from "../lib/tokens";

export interface DriverSelectProps {
  value: number | null;
  onChange: (driverId: number | null, driver: DriverListItem | null) => void;
}

export function DriverSelect({ value, onChange }: DriverSelectProps): React.JSX.Element {
  const { t } = useTranslation();
  const { apiClient } = useAuth();
  const [searchInput, setSearchInput] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [open, setOpen] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Debounce search input (300ms).
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const driversQuery = useQuery({
    queryKey: ["drivers", "select", debouncedSearch],
    queryFn: () =>
      driversApi.list(apiClient, {
        search: debouncedSearch || undefined,
        limit: 10,
      }),
    enabled: open || debouncedSearch.length > 0,
  });

  function handlePick(d: DriverListItem): void {
    onChange(d.id, d);
    setSearchInput(d.full_name ?? `Driver #${d.id}`);
    setOpen(false);
  }

  function handleClear(): void {
    onChange(null, null);
    setSearchInput("");
  }

  const items = driversQuery.data?.items ?? [];

  return (
    <div
      ref={containerRef}
      data-testid="driver-select"
      style={{ position: "relative", marginBottom: 12 }}
    >
      <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
        {t("dispatcher:newtrip.driver_label", "Driver")}
      </label>
      <div style={{ display: "flex", gap: 8 }}>
        <input
          type="text"
          value={searchInput}
          onChange={(e) => {
            setSearchInput(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={t("dispatcher:newtrip.driver_placeholder", "Search driver…") as string}
          data-testid="driver-select-input"
          style={{
            flex: 1,
            minWidth: 0,
            boxSizing: "border-box",
            padding: "10px 12px",
            minHeight: TOUCH_TARGET_MIN,
            fontSize: 16,
            border: "1px solid #ccc",
            borderRadius: 8,
          }}
        />
        {value !== null && (
          <button
            type="button"
            onClick={handleClear}
            data-testid="driver-select-clear"
            style={{
              minWidth: TOUCH_TARGET_MIN,
              minHeight: TOUCH_TARGET_MIN,
              border: "1px solid #ccc",
              borderRadius: 8,
              background: "white",
              fontSize: 14,
            }}
            aria-label={t("dispatcher:newtrip.driver_clear", "Clear driver") as string}
          >
            ×
          </button>
        )}
      </div>
      {open && items.length > 0 && (
        <ul
          data-testid="driver-select-suggestions"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            background: "white",
            border: "1px solid #ddd",
            borderRadius: 8,
            zIndex: 50,
            maxHeight: 240,
            overflowY: "auto",
            margin: 0,
            padding: 0,
            listStyle: "none",
          }}
        >
          {items.map((d) => (
            <li key={d.id} aria-selected={d.id === value} data-testid={`driver-option-${d.id}`}>
              <button
                type="button"
                onClick={() => handlePick(d)}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  padding: "10px 12px",
                  minHeight: TOUCH_TARGET_MIN,
                  background: "transparent",
                  border: "none",
                  borderBottom: "1px solid #eee",
                  fontSize: 16,
                  cursor: "pointer",
                }}
              >
                {d.full_name ?? `Driver #${d.id}`}
                {d.phone ? <small style={{ color: "#666" }}> · {d.phone}</small> : null}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
